/** @jsxImportSource @emotion/react */

import { captureException } from "@sentry/react";
import { useTasksTableGdtQuery } from "graphql/_Types";
import { EuiSkeletonText } from "@inscopix/ideas-eui";
import { CallOutError } from "components/CallOutError/CallOutError";
import { isEqual, orderBy } from "lodash";
import { Helmet } from "react-helmet-async";
import { useMemo } from "react";
import { formatInterval } from "utils/formatInterval";
import { isNonNull } from "utils/isNonNull";
import { roundToSignificant } from "utils/roundToSignificant";
import {
  TasksTable,
  TasksTableProps,
  TasksTableTask,
} from "pages/project/tasks//TasksTable";
import { isNonNullish } from "utils/isNonNullish";
import { isNullish } from "utils/isNullish";
import { ToolSpec } from "types/json-schemas/type_guards";
import { ToolVisibility } from "types/constants";
import { useDataTableContext } from "pages/gdt/store/DataTableProvider";

export const ProjectTabTasks = () => {
  const project = useDataTableContext((s) => {
    const project = s.project;
    return {
      id: project.id,
      name: project.name,
      isToolProject: project.is_tool_project,
    };
  }, isEqual);

  const { loading, data } = useTasksTableGdtQuery({
    variables: {
      filter: {
        projectId: { equalTo: project.id },
        cloned: {
          notEqualTo: true,
        },
      },
    },
    fetchPolicy: "cache-and-network",
    onError: (err) => captureException(err),
  });

  const tasks = useMemo(() => {
    const tasks = data?.allTasks?.nodes
      .map(
        ({
          id,
          analysisTableRow,
          outputGroup,
          credits,
          user,
          status,
          cloned,
          dateCreated,
          taskActivityByTaskId,
          toolVersion,
        }) => {
          const toolSpec = toolVersion?.toolSpec as ToolSpec | undefined | null;
          const table: TasksTableTask["table"] = (() => {
            const table = analysisTableRow?.table;

            if (analysisTableRow !== null && isNonNullish(table)) {
              const row = {
                ...analysisTableRow,
                deleted: analysisTableRow.dateDeleted !== null,
              };
              let name = table.name;
              if (row.dateDeleted !== null) {
                name += " (Row Deleted)";
              }

              return {
                ...table,
                name,
                resultsByKey: outputGroup?.outputGroupFiles.nodes
                  .flatMap(({ drsFile }) => drsFile)
                  .filter(isNonNull)
                  .reduce<Record<string, string>>((resultsByKey, file) => {
                    if (file.key !== null) {
                      resultsByKey[file.key] = file.id;
                    }
                    return resultsByKey;
                  }, {}),
                row,
              };
            }
          })();

          const interval = taskActivityByTaskId?.duration;

          const durationString = interval ? formatInterval(interval) : "";

          const isSystemUtil =
            toolVersion?.visibility === ToolVisibility.SYSTEM;

          const toolName = (() => {
            if (isNullish(toolSpec) || toolSpec.name === undefined) {
              return "Unknown";
            } else if (isSystemUtil) {
              return toolSpec.name;
            } else {
              let name = toolSpec.name;
              if (toolSpec.version !== undefined) {
                name += ` ${toolSpec.version}`;
              }
              return `${name} (Table Deleted)`;
            }
          })();

          const formattedTask: TasksTableProps["tasks"][number] = {
            id,
            credits: roundToSignificant(credits ?? 0),
            cloned,
            status,
            user,
            dateCreated,
            table,
            toolName,
            isSystemUtil,
            duration:
              interval !== undefined
                ? {
                    readableString: durationString,
                    interval,
                  }
                : undefined,
          };
          return formattedTask;
        },
      )
      .filter(isNonNull);
    if (tasks !== undefined) {
      return orderBy([...tasks], [(task) => task.dateCreated], ["desc"]);
    }
  }, [data?.allTasks?.nodes]);

  if (tasks) {
    return (
      <>
        <Helmet title={`${project.name}: All Tasks`} />
        <TasksTable tasks={tasks} suppressTableLinks={project.isToolProject} />
      </>
    );
  }

  if (loading) {
    return <EuiSkeletonText />;
  }

  return <CallOutError />;
};
